/**
 * Mock Blockchain Integration for Arariboia Biodiversity Credits
 * Simulates NFT-based grid cell ownership and biodiversity credit transactions
 */

export interface BlockchainOwnership {
  cellId: string;
  owner: string;
  ownerAddress: string;
  nftTokenId: string;
  purchaseDate: Date;
  purchasePrice: number;
  creditsPurchased: number;
}

export interface NFTCertificate {
  tokenId: string;
  cellId: string;
  coordinates: [number, number];
  areaHectares: number;
  bqiScore: number;
  carbonStock: number;
  speciesCount: number;
  mintDate: Date;
  imageUri: string;
  metadata: {
    standard: string; // ERC-721
    blockchain: string; // Polygon
    contractAddress: string;
  };
}

export interface Transaction {
  id: string;
  type: 'PURCHASE' | 'TRANSFER' | 'CREDIT_CLAIM';
  cellId: string;
  from: string;
  to: string;
  amount: number;
  credits: number;
  timestamp: Date;
  transactionHash: string;
  blockNumber: number;
}

/**
 * Mock wallet addresses for demo
 */
const MOCK_WALLETS = [
  { name: 'Conservation Trust', address: '0x742d35Cc6634C0532925a3b844Bc9e7595f0bEb' },
  { name: 'Green Investment Fund', address: '0x4E5B2ca8b5f0c5e3f8a9d1b2c3d4e5f6a7b8c9d0' },
  { name: 'Biodiversity DAO', address: '0x1A2B3C4D5E6F7G8H9I0J1K2L3M4N5O6P7Q8R9S0' },
  { name: 'Climate Impact Partners', address: '0x9F8E7D6C5B4A3928170615D4C3B2A1908F7E6D5' },
  { name: 'Rainforest Alliance', address: '0x5D4C3B2A1908F7E6D5C4B3A2918F7E6D5C4B3A29' }
];

/**
 * Generate mock blockchain ownership for 10% of cells
 */
export function generateBlockchainOwnership(cellId: string, cellIndex: number): BlockchainOwnership | null {
  // Only 10% of cells are owned
  const seededRandom = (seed: number) => {
    const x = Math.sin(seed * 7919) * 10000;
    return x - Math.floor(x);
  };

  if (seededRandom(cellIndex) > 0.1) {
    return null; // Cell not owned
  }

  const ownerIndex = Math.floor(seededRandom(cellIndex * 2) * MOCK_WALLETS.length);
  const owner = MOCK_WALLETS[ownerIndex];

  const daysAgo = Math.floor(seededRandom(cellIndex * 3) * 365);
  const purchaseDate = new Date();
  purchaseDate.setDate(purchaseDate.getDate() - daysAgo);

  const creditsPurchased = Math.round(80 + seededRandom(cellIndex * 4) * 170); // 80-250 credits
  const purchasePrice = Math.round(creditsPurchased * (12 + seededRandom(cellIndex * 5) * 8)); // $12-20 per credit

  return {
    cellId,
    owner: owner.name,
    ownerAddress: owner.address,
    nftTokenId: `BQI-ARB-${cellIndex.toString().padStart(6, '0')}`,
    purchaseDate,
    purchasePrice,
    creditsPurchased
  };
}

/**
 * Generate NFT certificate for a cell
 */
export function generateNFTCertificate(
  cellId: string,
  cellIndex: number,
  coordinates: [number, number],
  bqiScore: number,
  carbonStock: number,
  speciesCount: number
): NFTCertificate {
  const ownership = generateBlockchainOwnership(cellId, cellIndex);
  if (!ownership) {
    throw new Error('Cell is not owned');
  }

  return {
    tokenId: ownership.nftTokenId,
    cellId,
    coordinates,
    areaHectares: 1.0,
    bqiScore,
    carbonStock,
    speciesCount,
    mintDate: ownership.purchaseDate,
    imageUri: `ipfs://Qm${cellId.slice(-40)}/certificate.png`,
    metadata: {
      standard: 'ERC-721',
      blockchain: 'Polygon',
      contractAddress: '0xARAR1B01ABD1V3R51TYC3RT1F1C4T30N5Y5T3M01'
    }
  };
}

/**
 * Generate mock transaction history for a cell
 */
export function generateTransactionHistory(cellId: string, cellIndex: number): Transaction[] {
  const ownership = generateBlockchainOwnership(cellId, cellIndex);
  if (!ownership) {
    return []; // No transactions for unowned cells
  }

  const transactions: Transaction[] = [];

  // Initial purchase (minting)
  transactions.push({
    id: `tx-${cellIndex}-0`,
    type: 'PURCHASE',
    cellId,
    from: '0x0000000000000000000000000000000000000000', // Null address
    to: ownership.ownerAddress,
    amount: ownership.purchasePrice,
    credits: ownership.creditsPurchased,
    timestamp: ownership.purchaseDate,
    transactionHash: `0x${cellIndex.toString(16).padStart(64, '0')}`,
    blockNumber: 15000000 + cellIndex
  });

  // Possible credit claims (quarterly)
  const daysSincePurchase = Math.floor((Date.now() - ownership.purchaseDate.getTime()) / (1000 * 60 * 60 * 24));
  const quartersPassed = Math.floor(daysSincePurchase / 90);

  for (let i = 0; i < Math.min(quartersPassed, 4); i++) {
    const claimDate = new Date(ownership.purchaseDate);
    claimDate.setDate(claimDate.getDate() + (i + 1) * 90);

    const creditsClaimable = Math.round(ownership.creditsPurchased * 0.25);

    transactions.push({
      id: `tx-${cellIndex}-${i + 1}`,
      type: 'CREDIT_CLAIM',
      cellId,
      from: ownership.ownerAddress,
      to: ownership.ownerAddress,
      amount: 0,
      credits: creditsClaimable,
      timestamp: claimDate,
      transactionHash: `0x${(cellIndex + i * 1000).toString(16).padStart(64, '0')}`,
      blockNumber: 15000000 + cellIndex + (i * 100000)
    });
  }

  return transactions;
}

/**
 * Calculate total blockchain statistics
 */
export function calculateBlockchainStats(totalCells: number): {
  ownedCells: number;
  availableCells: number;
  totalValue: number;
  totalCredits: number;
  uniqueOwners: number;
} {
  let ownedCells = 0;
  let totalValue = 0;
  let totalCredits = 0;
  const ownerAddresses = new Set<string>();

  for (let i = 0; i < totalCells; i++) {
    const ownership = generateBlockchainOwnership(`cell-${i}`, i);
    if (ownership) {
      ownedCells++;
      totalValue += ownership.purchasePrice;
      totalCredits += ownership.creditsPurchased;
      ownerAddresses.add(ownership.ownerAddress);
    }
  }

  return {
    ownedCells,
    availableCells: totalCells - ownedCells,
    totalValue,
    totalCredits,
    uniqueOwners: ownerAddresses.size
  };
}

/**
 * Get ownership color for map overlay
 */
export function getOwnershipColor(ownership: BlockchainOwnership | null): string {
  if (!ownership) {
    return '#28a745'; // Green for available
  }

  // Different colors for different owners
  const colors = [
    '#1a2642', // Navy (Conservation Trust)
    '#c9a961', // Gold (Green Investment Fund)
    '#4287f5', // Blue (Biodiversity DAO)
    '#f542b0', // Pink (Climate Impact Partners)
    '#42f554'  // Lime (Rainforest Alliance)
  ];

  const ownerIndex = MOCK_WALLETS.findIndex(w => w.address === ownership.ownerAddress);
  return colors[ownerIndex] || '#666666';
}

/**
 * Format wallet address for display
 */
export function formatWalletAddress(address: string): string {
  return `${address.slice(0, 6)}...${address.slice(-4)}`;
}

/**
 * Format transaction hash for display
 */
export function formatTransactionHash(hash: string): string {
  return `${hash.slice(0, 10)}...${hash.slice(-8)}`;
}
